import {
  CHAT_BUFFER_LIMIT,
  CHAT_TEXT_LIMIT,
  chatSendSchema,
} from "@frontier/protocol/chat-contracts";
import type {
  ChatChannelId,
  ChatMessage,
  ChatPacket,
  ChatScope,
  ChatSend,
} from "@frontier/protocol/chat-contracts";

export interface ChatChannelState {
  scope: ChatScope | null;
  messages: readonly ChatMessage[];
  unread: number;
  draft: string;
  pending: boolean;
  error: string;
}
export interface ChatSnapshot {
  connected: boolean;
  contextId: string | null;
  selected: ChatChannelId;
  channels: Record<ChatChannelId, ChatChannelState>;
}
export interface ChatConnection {
  receive(packet: ChatPacket): void;
  disconnect(): void;
}
interface Attempt {
  command: ChatSend;
  revision: number;
  timer: ReturnType<typeof setTimeout> | null;
}
const CHANNELS: readonly ChatChannelId[] = [
  "global",
  "faction",
  "location",
  "party",
];
function emptyChannel(): ChatChannelState {
  return {
    scope: null,
    messages: [],
    unread: 0,
    draft: "",
    pending: false,
    error: "",
  };
}
export function emptyChat(): ChatSnapshot {
  return {
    connected: false,
    contextId: null,
    selected: "global",
    channels: {
      global: emptyChannel(),
      faction: emptyChannel(),
      location: emptyChannel(),
      party: emptyChannel(),
    },
  };
}
function createChatRequestId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const hex = Array.from(bytes, (byte, index) =>
    (index === 6 ? (byte & 15) | 64 : index === 8 ? (byte & 63) | 128 : byte)
      .toString(16)
      .padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** One connection owns four bounded feeds and at most one outstanding send per feed. */
export class ChatSession {
  private value = emptyChat();
  private epoch = 0;
  private playerId = "";
  private sendCommand: ((command: ChatSend) => void) | null = null;
  private reading: ChatChannelId | null = null;
  private readonly attempts = new Map<ChatChannelId, Attempt>();
  private readonly revisions: Record<ChatChannelId, number> = {
    global: 0,
    faction: 0,
    location: 0,
    party: 0,
  };

  constructor(
    private readonly publish: (snapshot: ChatSnapshot) => void,
    private readonly nextRequestId: () => string = createChatRequestId,
    private readonly timeoutMs = 8000,
  ) {}
  snapshot(): ChatSnapshot {
    return this.value;
  }
  private update(value: ChatSnapshot): void {
    this.value = value;
    this.publish(value);
  }
  private channel(id: ChatChannelId, channel: ChatChannelState): void {
    this.update({
      ...this.value,
      channels: { ...this.value.channels, [id]: channel },
    });
  }
  private clearAttempt(channel: ChatChannelId): void {
    const attempt = this.attempts.get(channel);
    if (attempt?.timer) clearTimeout(attempt.timer);
    this.attempts.delete(channel);
  }
  reset(): void {
    ++this.epoch;
    for (const channel of CHANNELS) {
      this.clearAttempt(channel);
      this.revisions[channel] = 0;
    }
    this.playerId = "";
    this.sendCommand = null;
    this.reading = null;
    this.update(emptyChat());
  }
  connect(playerId: string, send: (command: ChatSend) => void): ChatConnection {
    this.reset();
    this.playerId = playerId;
    this.sendCommand = send;
    this.update({ ...this.value, connected: true });
    const epoch = this.epoch;
    return {
      receive: (packet) => {
        if (epoch === this.epoch) this.receive(packet);
      },
      disconnect: () => {
        if (epoch === this.epoch) this.reset();
      },
    };
  }
  select(channel: ChatChannelId): void {
    if (channel === this.value.selected) return;
    this.reading = null;
    this.update({ ...this.value, selected: channel });
  }
  read(channel: ChatChannelId | null): void {
    this.reading = channel;
    if (channel && this.value.channels[channel].unread)
      this.channel(channel, { ...this.value.channels[channel], unread: 0 });
  }
  draft(channel: ChatChannelId, text: string): void {
    ++this.revisions[channel];
    this.channel(channel, {
      ...this.value.channels[channel],
      draft: text.slice(0, CHAT_TEXT_LIMIT),
      error: "",
    });
  }
  send(): boolean {
    const id = this.value.selected;
    const channel = this.value.channels[id];
    if (
      !this.value.connected ||
      !this.value.contextId ||
      !this.sendCommand ||
      !channel.scope ||
      channel.pending
    )
      return false;
    const previous = this.attempts.get(id);
    const same =
      previous &&
      previous.command.contextId === this.value.contextId &&
      previous.command.text === channel.draft.trim() &&
      previous.revision === this.revisions[id];
    const parsed = chatSendSchema.safeParse({
      requestId: same ? previous.command.requestId : this.nextRequestId(),
      contextId: this.value.contextId,
      channel: id,
      text: channel.draft,
    });
    if (!parsed.success) {
      this.channel(id, {
        ...channel,
        error: `Сообщение: 1–${CHAT_TEXT_LIMIT} символов без управляющих знаков.`,
      });
      return false;
    }
    this.clearAttempt(id);
    const attempt: Attempt = {
      command: parsed.data,
      revision: this.revisions[id],
      timer: null,
    };
    this.attempts.set(id, attempt);
    const epoch = this.epoch;
    attempt.timer = setTimeout(() => {
      if (epoch !== this.epoch || this.attempts.get(id) !== attempt) return;
      attempt.timer = null;
      this.channel(id, {
        ...this.value.channels[id],
        pending: false,
        error:
          "Нет подтверждения. Текст сохранён; проверь ленту перед повтором.",
      });
    }, this.timeoutMs);
    this.channel(id, { ...channel, pending: true, error: "" });
    try {
      this.sendCommand(parsed.data);
      return true;
    } catch {
      if (this.attempts.get(id) !== attempt) return false;
      if (attempt.timer) clearTimeout(attempt.timer);
      attempt.timer = null;
      this.channel(id, {
        ...this.value.channels[id],
        pending: false,
        error: "Не удалось отправить. Текст сохранён.",
      });
      return false;
    }
  }
  private message(message: ChatMessage): boolean {
    const channel = this.value.channels[message.channel];
    if (
      channel.scope?.scopeId !== message.scopeId ||
      channel.messages.some((item) => item.id === message.id)
    )
      return false;
    this.channel(message.channel, {
      ...channel,
      messages: [...channel.messages, message].slice(-CHAT_BUFFER_LIMIT),
      unread:
        message.senderId === this.playerId || this.reading === message.channel
          ? channel.unread
          : Math.min(CHAT_BUFFER_LIMIT, channel.unread + 1),
    });
    return true;
  }
  private receive(packet: ChatPacket): void {
    if (packet.type === "context") {
      if (this.value.contextId === packet.contextId) return;
      const channels = { ...this.value.channels };
      for (const id of CHANNELS) {
        const scope = packet.scopes.find((item) => item.channel === id) ?? null;
        const before = channels[id];
        const attempted = this.attempts.has(id);
        this.clearAttempt(id);
        channels[id] =
          scope && before.scope?.scopeId === scope.scopeId
            ? {
                ...before,
                scope,
                pending: false,
                error: attempted
                  ? "Канал обновлён. Текст сохранён, отправь его заново."
                  : before.error,
              }
            : { ...emptyChannel(), scope };
        ++this.revisions[id];
      }
      this.update({ ...this.value, contextId: packet.contextId, channels });
      return;
    }
    if (!this.value.contextId || packet.contextId !== this.value.contextId)
      return;
    if (packet.type === "message") {
      this.message(packet.message);
      return;
    }
    if (packet.type === "accepted") {
      this.message(packet.message);
      const id = packet.message.channel;
      const attempt = this.attempts.get(id);
      if (
        !attempt ||
        attempt.command.requestId !== packet.requestId ||
        packet.message.senderId !== this.playerId ||
        this.value.channels[id].scope?.scopeId !== packet.message.scopeId
      )
        return;
      this.clearAttempt(id);
      const channel = this.value.channels[id];
      const unchanged = attempt.revision === this.revisions[id];
      this.channel(id, {
        ...channel,
        pending: false,
        error: "",
        draft: unchanged ? "" : channel.draft,
      });
      return;
    }
    for (const [id, attempt] of this.attempts) {
      if (attempt.command.requestId !== packet.requestId) continue;
      if (packet.code === "CHAT_UNAVAILABLE") {
        if (attempt.timer) clearTimeout(attempt.timer);
        attempt.timer = null;
      } else this.clearAttempt(id);
      this.channel(id, {
        ...this.value.channels[id],
        pending: false,
        error: packet.message,
      });
      break;
    }
  }
}
